export default {
  en: {
    controlTypes: {
      yes: 'Yes',
      no: 'No',
      placeholder: 'Enter a number',
    },
    orgAssessmentTitle: 'Assessment',
    orgReassessmentTitle: 'Reassessment',
    scoping: {
      pageTitle: 'Scoping questions',
      intro: {
        title: 'Tailor your assessment.',
        text: "We'll ask a few short questions to ensure your security assessment reflects your organisation's responsibilities. You can revise your answers at any time.",
        time: 'Takes 4 minutes',
        cta: 'Start',
      },
      question: 'Question {x} of {y}',
      success: {
        title: 'Your questions are ready',
        text: 'Thanks for scoping! Your assessment is now set up, you can start working through it at your own pace.',
        cta: 'Continue to assessment',
      },
    },
    evidence: {
      selectButton: 'Select evidence',
    },
    wizardOverview: {
      submitFailed: 'There was an error submitting the assessment.',
    },
    domainProgress: '{completed} / {total} completed',
    reassessmentProgress: '{completed} / {total} confirmed',
    domainProgressDomains: '{completed} / {total} completed domains',
    reassessmentProgressDomains: '{completed} / {total} confirmed domains',
    messaging: {
      personalising: {
        description:
          "Answer the questions below so that we can keep your security assessment relevant to your organisation. If you're unsure, don't worry - you can always change your answers later.",
      },
    },
    assessmentOverview: {
      notStarted: 'Not started',
      percentageComplete: '{percentage}% complete',
      profileComplete: 'Profile complete',
      title: 'Assessment overview',
      pdfExport: 'Export PDF',
      pdfExportTooltip: 'Export a copy of the security assessment as a PDF',
      addColleaguesText: 'Need help filling your profile out?',
      downloadPDF: 'Download PDF',
      shareProfile: 'Share profile',
      share: 'Share',
      sharing: 'Sharing',
      shareTooltip: 'Share assessment',
      sharingDesc:
        'Proactively <b>share your profile</b> with your clients to reduce questionnaire burden and impress your prospects.',
      viewOverview: 'View all domains in card view',
      viewTable: 'View all controls in table view',
      search: 'Search',
      scoping: 'Scoping questions',
      allDomains: 'All domains',
      deprecated: 'Deprecated controls',
      showOOSControls: '{0} controls out of scope. Show anyway',
      hideOOSControls: 'Showing {0} controls out of scope. Hide',
      searchEmptyTitle: 'No matching controls',
      searchEmptySubtitle: 'Try a different search term',
      editOOS:
        'The scoping question for this domain needs to be answered differently to bring the control into scope for answering',
      whatsNext: "What's next",
      startReassessment: {
        error: 'There was an error starting your reassessment.',
      },
      yourAssessment: {
        domainLetter: 'Domain {0}',
      },
    },
    answer: {
      notesPlaceholder: 'Add further details to your answer here...',
      addUserControl: 'Is a colleague better suited to answer this control?',
      addColleague: 'Add colleague',
      addUserScoping: 'Is a colleague better suited to answer this scoping question?',
      incompleteTooltip:
        "Mark this answer as complete when you're ready.\nYou can move on and come back later if needed.",
      completedTooltip:
        'This answer is marked as complete.\nBut you can still edit it above at any time.',
      unconfirmedTooltip:
        "Mark this answer as confirmed when you're ready.\nYou can move on and come back later if needed.",
      confirmedTooltip:
        'This answer is marked as confirmed.\nBut you can still edit it above at any time.',
      scanEvidence: 'Scan evidence',
    },
    bookmarks: {
      bookmark: 'Bookmark',
      bookmarked: 'Bookmarked',
      bookmarkingFailed: 'There was an error bookmarking the control. Try taking the action again.',
      unbookmarkingFailed: 'There was an error un-bookmarking the control. Please try again.',
    },
    unconfirmedAnswer: {
      title: 'Unconfirmed answer.',
      questionTitle: 'Updated question',
      description:
        'This control question has been updated since you last reviewed your answer. Please confirm it is still accurate.',
      questionDescription:
        'The meaning of this question has been updated since you last reviewed it.\nPlease take a look at your answer to check that it is still accurate.',
      confirm: 'Confirm',
      confirmFail: 'Confirming answer has failed',
    },
    misc: {
      loadingError:
        'An error occurred while loading data from the server. Please try refreshing the page.',
      saveError: 'There was an error saving the scoping question answers.',
      continue: 'Continue',
      retry: 'Retry?',
      domain: 'DOMAIN',
      completeq: 'Complete?',
      complete: 'Complete',
      completed: 'Completed',
      confirmq: 'Confirm?',
      confirmAnswer: 'Confirm answer',
      confirm: 'Confirmed',
      confirmDomain: 'Confirm domain',
      confirmDomainTooltip: 'Select to confirm all questions within this domain',
      confirmDomainFailure: 'Failed to confirm one or more controls.',
      confirmDomainDisabledTooltip:
        "You'll need to answer any new questions within this domain before you can confirm all questions",
      confirmDomainAlreadyConfirmedTooltip:
        'You have already confirmed all questions within this domain',
      outOfScope: 'Out of scope',
      notApplicable: 'Not applicable',
      gotoKB: 'Need help with this control?',
      learnMore: 'Learn more about this question',
      gotoScopingKB: 'Need help with this scoping question?',
      scopingQuestion: 'SCOPING QUESTION',
      unanswered: 'unanswered',
      save: 'Save',
      search: 'Search questions',
      genericSavingError: 'An error occurred while saving. Please try again.',
      previous: 'Previous',
      next: 'Next',
      invalidated: 'Invalidated',
      expired: 'Expired',
      expiresTime: 'Expires {0}',
      copySuccess: 'Copied successfully',
      invalidateLink: 'Invalidate',
      shortcuts: {
        title: 'Keyboard shortcuts',
        text: 'Use the left and right arrow keys to move between domains.',
      },
      answered: 'Answered',
      deprecated: 'Deprecated',
      deprecatedControls: 'Deprecated controls',
      goToDeprecatedControls: 'Go to deprecated controls',
      laterButton: 'Later',
      addUser: 'Add user',
      copyLink: 'Copy link',
      removeAnswer: 'Clear answer',
      removeProductAnswer: 'Remove product answer',
    },
    wizard: {
      title: 'Assessment wizard',
      subTitle: '/ {domainTitle}',
      search: 'Search',
      bookmarks: 'Bookmarks',
      bookmarksPlaceholder: 'Bookmark questions to revisit later',
      bookmarksPlaceholderSubtitle:
        'When answering a question, you can bookmark it to revisit later, upload evidence or flag it for another member of your team',
      attentionPlaceholder: 'There are no updated controls that require your attention right now.',
      attention: 'Requires attention',
      all: 'All questions',
      requiredQuestions: 'All required questions',
      required: 'Required',
      notRequired: 'Not required',
      domainNotRequiredTitle: 'You are not required to answer this domain',
      domainNotRequiredText:
        'You can still choose to fill these questions out if you would like to share the answers with clients',
      additionalQuestionsTitle:
        'There is 1 additional question you have not been required to answer | There are {n} additional questions you have not been required to answer',
      additionalQuestionsText:
        'You can still choose to fill these out if you would like to share the answers with other clients',
      hideNonRequiredQuestions: 'Hide questions not required by clients',
      domain: 'DOMAIN {0}',
      controlCount: '<b>{n}</b> control | <b>{n}</b> controls',
      questionCount: '{n} question | {n} questions',
      questionsRequiredCount: '{n} required question | {n} required questions',
      completionTime: 'About <b>{0} minutes</b>',
      completion: '{0}% complete',
      answered: '{0}% answered',
      confirmed: '{0}% confirmed',
      bookmarkHero: '{n} bookmark | {n} bookmarks',
      scoping: 'Scoping questions',
      submitTitle: 'Submit your assessment',
      submitSubtitle:
        "You're almost there - submit to give visibility to your clients & unlock your free continuous profile.",
      reassessmentSubmitTitle: 'Submit your reassessment',
      reassessmentSubmitSubtitle:
        "You're almost there! You've reviewed and confirmed all your answers - all you need to do now is submit your reassessment. When you do, your clients will be notified and can review any changes you've made.",
      submitButton: 'Submit assessment',
      submitReassessmentButton: 'Submit reassessment',
      nextDomain: 'Next domain',
      productTitle: 'In relation to {product}, {question}',
      selectProduct:
        'Do you want to answer this question for a specific product? | Do you want to answer these questions for a specific product?',
      submitPrompt: {
        title: 'Before you submit',
        questionsNumber: '{n} question | {n} questions',
        bookmarksPrompt:
          "Just a heads up, it looks like you've got {bookmarkCount} bookmarked. Would you like to continue and submit your assessment or review these questions first?",
        attentionPrompt:
          "Just a heads up, it looks like you've got {attentionCount} to confirm. The meaning of these questions has been updated since you last reviewed them. Would you like to continue and submit your assessment or confirm these questions first?",
        bothPrompt:
          "Just a heads up, it looks like you've got {attentionCount} to confirm and {bookmarkCount} bookmarked. Would you like to continue and submit your assessment or confirm these questions first?",
        submitButton: 'Submit assessment',
        submitReassessmentButton: 'Submit reassessment',
        bookmarksButton: 'View bookmarks',
        attentionButton: 'Confirm answers',
        allButton: 'View all questions',
      },
      filters: {
        all: 'All',
        incomplete: 'Incomplete',
        complete: 'Complete',
        emptyTitle: 'No controls',
        emptyQuestionTitle: 'No questions',
        emptySubtitle: 'No controls currently match your selected filters',
        emptyQuestionSubtitle: 'No questions currently match your selected filters',
        confirmed: 'Confirmed',
        unconfirmed: 'Unconfirmed',
        answered: 'Answered',
        unanswered: 'Unanswered',
      },
      quickAnswer: {
        generatingTitle: 'Currently generating suggestions for this domain',
        generatingTitleOverview: 'Currently generating suggestions for your assessment',
        generatingDesc: 'You can begin answering questions in the meantime',
        apply: 'Apply suggestion | Apply suggestions',
        applyTitle:
          'There is 1 suggestion for the {unansweredCount} unanswered controls in this domain | There are {n} suggestions for the {unansweredCount} unanswered controls in this domain',
        applyDesc: 'Would you like to apply these suggestions?',
        responseAvailable: 'Suggested response available',
        responseAvailableTooltip:
          "A suggestion is available for this control based on the documents you've uploaded.",
        applySuggestion: 'Apply suggestion',
        suggestedResponse: 'Suggested response',
        furtherDetails: 'Further details',
        generatedFrom:
          'This suggestion was generated from information found in {suggestionCount} of your evidence documents:',
        suggestionFromCount: 'Suggestion from {0}',
        generatedCount: '{n} document | {n} documents',
      },
    },
    singleDomainTableView: {
      title: 'Not required',
      text: 'You can still choose to fill these out if you would like to share the answers with other clients',
    },
    row: {
      unanswered: 'Unanswered',
      notApplicable: 'Not applicable',
      outOfScope: 'Out of scope',
      inRelationTo: 'In relation to {product}',
      tooltip: {
        evidence: 'Evidence uploaded',
        answer: 'Answer',
        unanswered: "This control hasn't been answered",
        outOfScope: "This control's domain is out of scope",
        notApplicable:
          "Your answer to this control's parent control means this control is not applicable",
        unconfirmed: 'This control has been updated and is unconfirmed',
        bookmarked: 'This control is bookmarked',
      },
    },
    expandedRow: {
      save: 'Save',
      saveFailed: 'Failed to save your answer. Try again or contact Risk Ledger support.',
    },
    shareAssessment: {
      title: 'Share assessment',
      description_1:
        'You can share your assessment with other organisations outside of Risk Ledger who may ask you for assurance, reducing the number of questionnaires you must complete. Only answers and notes are shared, but not any evidence.',
      description_2:
        "The button below will generate a new link to your assessment. You can also provide a contact's email address for us to send them the new link.",
      description_3:
        'A link expires in 1 month, but can be invalidated earlier (for example, if one is shared accidentally).',
      existingLinks: 'Existing links',
      invalidatingFailed: 'Invalidating the link failed.',
      sharedAssessmentSuccess: 'Shared link generated',
      sharedAssessmentSuccessSent: 'Shared link generated and sent',
      email: 'Email address...',
      failedSharing: 'Failed to share assessment with {0}. Please try again.',
      failedGenerating: 'Failed to generate link. Please try again.',
      shareLink: 'Share link',
      generateLink: 'Generate link',
      failedGettingSharedAssessmentLinks: 'Failed getting shared assessment links',
    },
    startReassessmentPane: {
      title: 'Start your reassessment',
      subTitle:
        "We'll ask you to simply confirm the answers you provided previously and answer any new questions.\nPlease aim to submit it by {overdueDate} for your clients.",
      overdueSubTitle:
        "Your reassessment is now <b>overdue</b> and it needs to be <b>started</b> as soon as possible. We'll ask you to simply confirm the answers you provided previously and answer any new questions.",
      startButton: "Let's get started",
    },
    reassessment: {
      lastAssessmentDate: 'Last assessment',
      nextReassessmentDueDate: 'Next re-assessment due',
      status: {
        idle: 'Complete',
        reassessment_due: 'Due',
        reassessment_overdue: 'Overdue',
        reassessment_inprogress: 'In progress',
        initial_inprogress: 'Initial assessment in progress',
      },
      nextDue: 'Next due {dueDate}',
    },
    pdfExport: {
      title: 'Risk Ledger assessment',
      lastAssessment: 'Last assessment',
      nextAssessment: 'Next re-assessment',
      dateFormat: 'yyyy/MM/dd',
      generated: 'Generated at {0}',
      dateTimeFormat: 'hh:mma yyyy/MM/dd',
      scopingQuestion: 'Scoping question:',
      scopingNotes: 'Scoping notes:',
      question: 'Question',
      answer: 'Answer',
      scopingAnswer: 'Answer: {0}',
      notes: 'Answer notes:',
      lastModified: 'Last modified:',
      evidence: 'Evidence:',
      yes: 'Yes',
      no: 'No',
      outOfScope: 'Out of scope',
      notApplicable: 'Not applicable',
      unanswered: 'Unanswered',
      inRelationTo: 'In relation to:',
    },
    quickAnswer: {
      suggestionsTooltip:
        '{n} answer suggestion has been generated for this section | {n} answer suggestions have been generated for this section',
      suggestionsCount: '{n} suggestion | {n} suggestions',
    },
    quickAnswerDeleteDocs: {
      deleteModalTitle: 'Delete document "{name}"?',
      deleteModalDescription: "Please confirm if you would like to delete '{name}'.",
    },
    postAssessmentModal: {
      step1Title: 'Your security profile is ready',
      step1Text: 'You’ve shared your security posture with your client',
      step1Li1: 'They will now review and assess your profile',
      step1Li2: 'Share with other clients - no need to start from scratch',
      step2Title: 'Why does your client use Risk Ledger?',
      step2Li1: 'To get visibility of up to 95% of their suppliers*',
      step2Li2: 'They can spot hidden risks in their supply chain',
      step2TitleSecondary: 'Want to identify and address risks in your supply chain?',
      step2Li1Secondary: 'You can gain visibility of up to 95% of your suppliers*',
      step2Li2Secondary: 'You can spot hidden risks in your supply chain',
      step2Text: '*Based on usage by Schroders Personal Wealth (2024)',
      step3Title: 'Review and remediate supplier assessments. Effortlessly.',
      step3Li1: 'Get rid of manual review processes using spreadsheets',
      step3Li2: 'Start discussion, raise risks, and request remediations',
      step3Li3: 'Identify areas of non-compliance quickly and easily',
      step4Title: 'Spot risks before they become problems',
      step4Li1: 'Gain a 360 view of your risks & security posture',
      step4Li2: "Use suppliers' compliance score to identify the highest risks",
      step4Li3: 'Export the reports that show what you have done',
      step5Title: 'See your supply chain the way your client does',
      step5Li1: 'Gain instant access to an 8,000+ supplier network',
      step5Li2: 'Centralise and standardise your supplier reviews',
      step5Li3: 'Identify hidden risks in your supply chain',
      step5Help: 'Need help?',
      step5HelpCTA: 'Get in touch with our support team',
      step5BookCTA: 'Book kickoff call',
      step5Marketing: 'By booking, you consent to receive Risk Ledger communications.',
      step6Title: 'Stay in the loop',
      step6Li1: 'Learn best practices to get the most from Risk Ledger',
      step6Li2: 'See product news and feature updates as they launch',
      step6CTA: 'Take a look',
      step6Marketing: "By clicking {button}, you'll receive tips on using Risk Ledger.",
    },
  },
};
